import { Backdrop, Box, Button, CircularProgress, Stack } from "@mui/material";
import { PortalSelection } from "./components/PortalSelection";
import { UserFilters } from "./components/UserFilters";
import { ReportCofiguration } from "./components/ReportConfiguration";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  getReportTemplateDdlResult,
  Operator,
  ReportTemplateTypeEnum,
  ReportTemplateBodyParams,
} from "../../../services/api/types/reports/reports";
import {
  createUpdateReportTemplate,
  getReportTemplateById,
  getReportTemplateDdl,
  getUserFilters,
} from "../../../services/api/apiClient";
import { AssignmentFilters } from "./components/AssignmentFilters";
import { mapBackendToUI, transformReportTemplate } from "./utils/helpers";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  BooleanOperator,
  DateOperator,
  GetUserResult,
  ListOperator,
  NumericOperator,
  StringOperator,
} from "../../../services/api/types/users/users";
import { ValuesModal } from "../../UserManagement/Users/components/ValuesModal";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import { useUnsavedChanges } from "./hooks/useUnsavedChanges";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { PreviewReportModal } from "./components/PreviewReportModal";
import { brand } from "../../../theme/themePrimitives";
import { getShadeColour } from "../../../utils/helpers";
import dayjs from "dayjs";

export const NewReportTemplate: React.FC = () => {
  const [reportTemplateData, setReportTemplateData] = useState({
    reportConfiguration: {
      reportType: "",
      reportName: "",
    },
    portalSelection: {
      portals: [] as string[],
      groupResultsByPortal: false,
    },
    userSpecificFilters: {
      userFilters: [] as Array<{
        attribute: string;
        operator: Operator | "";
        value: string;
      }>,
      activityType: [] as string[],
      activityDate: "",
      filterByTargetUser: false,
    },
    assignmentFilters: {
      assignments: [] as string[],
      assignmentsStatus: [] as string[],
      progressStatus: [] as string[],
      dateFilters: [] as Array<{
        attribute: string;
        operator: Operator | "";
        date: string;
        startDate: string;
        endDate: string;
      }>,
    },
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [reportConfigExpanded, setReportConfigExpanded] = useState(false);
  const [portalSelectionExpanded, setPortalSelectionExpanded] = useState(false);
  const [userFiltersExpanded, setUserFiltersExpanded] = useState(false);
  const [assignmentFiltersExpanded, setAssignmentFiltersExpanded] =
    useState(false);
  const [reportTemplateDdlResult, setReportTemplateDdlResult] =
    useState<getReportTemplateDdlResult | null>(null);
  const [openValuesModal, setOpenValuesModal] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [valuesLoading, setValuesLoading] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>();
  const [currentFilterIndex, setCurrentFilterIndex] = useState<number>(0);
  const [currentFilterValue, setCurrentFilterValue] = useState<string>("");
  const [selectedOperator, setSelectedOperator] = useState<
    | StringOperator
    | NumericOperator
    | DateOperator
    | BooleanOperator
    | ListOperator
    | ""
  >("");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTemplateData, setPreviewTemplateData] =
    useState<ReportTemplateBodyParams | null>(null);

  const { showToast } = useToast();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [pageLoading, setPageLoading] = useState(!!state?.ReportTemplateUid);
  const { auth } = useAuth();
  const { portalUid } = useParams<{ portalUid: string }>();

  // Use the custom hook for unsaved changes
  const {
    showModal,
    setShowModal,
    handleConfirmNavigation,
    markSaving,
    setInitialData,
    handleCancelNavigation,
  } = useUnsavedChanges({
    currentData: reportTemplateData,
    isEditMode,
    shouldTrackInitialData: !!reportTemplateDdlResult,
  });

  // Detect edit mode early
  useEffect(() => {
    if (state?.ReportTemplateUid) {
      setIsEditMode(true);
      setReportConfigExpanded(true);
      setPortalSelectionExpanded(true);
      setUserFiltersExpanded(true);
      setAssignmentFiltersExpanded(true);
    }
  }, [state]);

  // Always fetch DDL (needed in both new and edit)
  useEffect(() => {
    if (!portalUid) return;
    fetchReportTemplateDdl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUid]);

  // Only fetch template by ID when:
  // - We are in edit mode
  // - The DDL is already loaded
  useEffect(() => {
    if (!portalUid) return;
    if (!isEditMode) return;
    if (!reportTemplateDdlResult) return;

    fetchReportTemplateById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUid, isEditMode, reportTemplateDdlResult]);

  // Pre-fill user activity date range for the User Activity report type
  useEffect(() => {
    const isUserActivityReport =
      reportTemplateData.reportConfiguration.reportType ===
      ReportTemplateTypeEnum.USER_ACTIVITY_REPORT;

    const [currentFrom, currentTo] =
      reportTemplateData.userSpecificFilters.activityDate?.split("|") || [
        "",
        "",
      ];

    if (!isUserActivityReport) {
      setReportTemplateData((prev) => ({
        ...prev,
        userSpecificFilters: {
          ...prev.userSpecificFilters,
          activityDate: "",
        },
      }));
    }

    if (isUserActivityReport && (!currentFrom || !currentTo)) {
      const today = dayjs();
      const dateTo = today.format("DD-MMM-YYYY");
      const dateFrom = today.subtract(7, "day").format("DD-MMM-YYYY");

      setReportTemplateData((prev) => ({
        ...prev,
        userSpecificFilters: {
          ...prev.userSpecificFilters,
          activityDate: `${dateFrom}|${dateTo}`,
        },
      }));
    }
  }, [
    reportTemplateData.reportConfiguration.reportType,
    reportTemplateData.userSpecificFilters.activityDate,
  ]);

  // Clear assignment filters when the User activity report type is selected,
  // since the AssignmentFilters section is hidden for that report type.
  useEffect(() => {
    const isUserActivityReport =
      reportTemplateData.reportConfiguration.reportType ===
      ReportTemplateTypeEnum.USER_ACTIVITY_REPORT;

    if (!isUserActivityReport) return;

    const { assignments, assignmentsStatus, progressStatus, dateFilters } =
      reportTemplateData.assignmentFilters;
    const hasValues =
      assignments.length > 0 ||
      assignmentsStatus.length > 0 ||
      progressStatus.length > 0 ||
      dateFilters.some((f) => f.attribute && f.operator);

    if (!hasValues) return;

    setReportTemplateData((prev) => ({
      ...prev,
      assignmentFilters: {
        assignments: [],
        assignmentsStatus: [],
        progressStatus: [],
        dateFilters: [],
      },
    }));
  }, [
    reportTemplateData.reportConfiguration.reportType,
    reportTemplateData.assignmentFilters,
  ]);

  const fetchReportTemplateDdl = async () => {
    if (!portalUid) return;

    const response = await getReportTemplateDdl(portalUid);
    setReportTemplateDdlResult(response);
  };

  const fetchReportTemplateById = async () => {
    if (!portalUid) return;

    try {
      const reportTemplateUid = state?.ReportTemplateUid;
      const response = await getReportTemplateById(
        portalUid,
        reportTemplateUid,
      );
      const uiData = mapBackendToUI(response, reportTemplateDdlResult!);
      setReportTemplateData(uiData);
      setInitialData(uiData);
    } catch (error) {
      console.error("Failed to load report template:", error);
    } finally {
      setPageLoading(false);
    }
  };

  const handleUpdateReportConfiguration = (data: {
    reportType?: string;
    reportName?: string;
  }) =>
    setReportTemplateData((prev) => ({
      ...prev,
      reportConfiguration: { ...prev.reportConfiguration, ...data },
    }));

  const handleUpdatePortalSelection = (data: {
    portals?: string[];
    groupResultsByPortal?: boolean;
  }) =>
    setReportTemplateData((prev) => ({
      ...prev,
      portalSelection: { ...prev.portalSelection, ...data },
    }));

  const handleUpdateUserFilters = (data: {
    userFilters?: Array<{
      attribute: string;
      operator: Operator | "";
      value: string;
    }>;
    status?: string[];
    activityType?: string[];
    activityDate?: string;
    filterByTargetUser?: boolean;
  }) =>
    setReportTemplateData((prev) => ({
      ...prev,
      userSpecificFilters: { ...prev.userSpecificFilters, ...data },
    }));

  const handleUpdateAssignments = (data: {
    assignments?: string[];
    assignmentsStatus?: string[];
    progressStatus?: string[];
    dateFilters?: {
      attribute: string;
      operator: Operator | "";
      date: string;
      startDate: string;
      endDate: string;
    }[];
  }) => {
    setReportTemplateData((prev) => ({
      ...prev,
      assignmentFilters: {
        ...prev.assignmentFilters,
        ...data,
      },
    }));
  };

  const handleSaveReport = async () => {
    const UserPortalUid = auth.user?.UserPortalUid;
    if (!UserPortalUid) return;
    const hasReportTypeError =
      !reportTemplateData.reportConfiguration.reportType.trim();
    const hasReportNameError =
      !reportTemplateData.reportConfiguration.reportName.trim();
    const hasPortalsError =
      reportTemplateData.portalSelection.portals.length === 0;

    const isValid = validateFields();
    if (!isValid) {
      if (hasReportTypeError || hasReportNameError)
        setReportConfigExpanded(true);
      if (hasPortalsError) setPortalSelectionExpanded(true);
      return;
    }

    try {
      setSaveLoading(true);
      markSaving(); // Mark that we're saving
      // 1. transform to backend structure
      const transformedData = transformReportTemplate(
        reportTemplateData,
        reportTemplateDdlResult!,
      );

      // 2. If editing - append template UID
      if (isEditMode && state?.ReportTemplateUid) {
        transformedData.reportTemplateUid = state.ReportTemplateUid;
      }

      // 3. Make the API call
      await createUpdateReportTemplate(portalUid!, transformedData);

      isEditMode
        ? showToast("Successfully updated report template", "success")
        : showToast("Successfully added new report template", "success");

      // Update initial data before navigation
      setInitialData(reportTemplateData);
      navigate(`/portal-configuration/${portalUid}/reports`);
    } catch (error: any) {
      showToast(error.response.data, "error");
      console.error("Failed to save report:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  const validateFields = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!reportTemplateData.reportConfiguration.reportType.trim())
      newErrors.reportType = "Report type is required.";
    if (!reportTemplateData.reportConfiguration.reportName.trim())
      newErrors.reportName = "Report name is required.";
    if (reportTemplateData.portalSelection.portals.length === 0)
      newErrors.portals = "At least one portal is required.";

    setErrors(newErrors);

    // Return true if valid
    return Object.keys(newErrors).length === 0;
  };

  const useClearError =
    (setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>) =>
    (key: string) =>
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const { [key]: _omit, ...rest } = prev;
        return rest;
      });

  const clearError = useClearError(setErrors);

  const getValuesForAttribute = async (
    attribute: string,
    operator:
      | StringOperator
      | NumericOperator
      | DateOperator
      | BooleanOperator
      | ListOperator
      | "",
    value: string,
    filterIndex: number,
  ) => {
    // Validate that attribute is selected
    if (!attribute.trim()) {
      showToast("Please select an attribute first", "error");
      return;
    }

    try {
      if (portalUid) {
        setCurrentFilterIndex(filterIndex);
        setCurrentFilterValue(value);
        setSelectedOperator(operator);
        setOpenValuesModal(true);
        setValuesLoading(true);

        const response: Partial<GetUserResult>[] = await getUserFilters(
          portalUid,
          {
            userAttributeKey: attribute,
            userAttributeValue: value,
            operator: operator,
          },
        );

        const valuesArray = response
          .map((item) => item[attribute as keyof GetUserResult])
          .filter(
            (value): value is string =>
              typeof value === "string" &&
              value !== null &&
              value !== undefined,
          )
          .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

        setSelectedValues(valuesArray);
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(error?.response?.data || "Failed to fetch values", "error");
    } finally {
      setValuesLoading(false);
    }
  };

  const addSelectedValuesToFilter = (selectedValues: string[]) => {
    const currentFilters = [
      ...reportTemplateData.userSpecificFilters.userFilters,
    ];
    const currentFilter = currentFilters[currentFilterIndex];

    currentFilters[currentFilterIndex] = {
      ...currentFilter,
      value: selectedValues.join("|"),
    };

    handleUpdateUserFilters({ userFilters: currentFilters });
    setOpenValuesModal(false);
  };

  const handleViewReport = () => {
    const UserPortalUid = auth.user?.UserPortalUid;
    if (!UserPortalUid || !reportTemplateDdlResult) {
      return;
    }

    const isValid = validateFields();
    if (!isValid) {
      showToast("Please fix validation errors before previewing", "error");
      return;
    }

    // Transform to backend structure
    const transformedData = transformReportTemplate(
      reportTemplateData,
      reportTemplateDdlResult,
    );

    // Set state and open modal
    setPreviewTemplateData(transformedData);
    setPreviewModalOpen(true);
  };

  usePageMetadata(
    {
      title: isEditMode ? "Edit report" : "Add new report",
      breadcrumbs: [
        {
          label: "Analytics",
          path: `/portal-configuration/${portalUid}/dashboard`,
        },
        {
          label: "Reports",
          path: `/portal-configuration/${portalUid}/reports`,
        },
        {
          label: isEditMode ? "Edit report" : "Add new report",
        },
      ],
    },
    [portalUid, isEditMode],
  );

  return (
    <Box>
      <Backdrop
        open={pageLoading}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: "#fff" }}
      >
        <CircularProgress color="inherit" size={60} />
      </Backdrop>
      <Stack gap={1}>
        <ReportCofiguration
          reportTypeOptions={
            reportTemplateDdlResult && reportTemplateDdlResult.reportType
          }
          value={reportTemplateData.reportConfiguration}
          onChange={(data) => {
            handleUpdateReportConfiguration(data);
            if (data.reportType) clearError("reportType");
            if (data.reportName) clearError("reportName");
          }}
          isExpanded={reportConfigExpanded}
          errors={errors}
        />
        <PortalSelection
          portalOptions={reportTemplateDdlResult?.portal || []}
          value={reportTemplateData.portalSelection}
          onChange={(data) => {
            handleUpdatePortalSelection(data);
            if (data.portals) clearError("portals");
          }}
          isExpanded={portalSelectionExpanded}
          errors={errors}
        />
        <UserFilters
          value={reportTemplateData.userSpecificFilters}
          onChange={handleUpdateUserFilters}
          userAttributeOptions={reportTemplateDdlResult?.userAttribute || []}
          userActionTypeOptions={
            reportTemplateDdlResult && reportTemplateDdlResult.userActionType
          }
          getValuesForAttribute={getValuesForAttribute}
          reportType={reportTemplateData.reportConfiguration.reportType}
          isExpanded={userFiltersExpanded}
        />
        {reportTemplateData.reportConfiguration.reportType !==
          ReportTemplateTypeEnum.USER_ACTIVITY_REPORT && (
          <AssignmentFilters
            value={reportTemplateData.assignmentFilters}
            assignmentsOptions={
              reportTemplateDdlResult &&
              reportTemplateDdlResult.userAssignment
            }
            assignmentsStatusOptions={
              reportTemplateDdlResult &&
              reportTemplateDdlResult.userAssignmentStatus
            }
            progressStatusOptions={
              reportTemplateDdlResult &&
              reportTemplateDdlResult.progressStatus
            }
            isExpanded={assignmentFiltersExpanded}
            onChange={handleUpdateAssignments}
          />
        )}
      </Stack>
      <Stack direction="row" justifyContent="center" gap={1} mt={3}>
        <Button
          sx={{
            px: 2,
            borderRadius: "12px",
            backgroundColor: `#ffff !important`,
            border: "1px solid #E5E7EB",
            fontWeight: "bold",
            "&:hover": {
              bgcolor: `${getShadeColour("#ffffff"!, -8)} !important`,
              fontWeight: 700,
            },
          }}
          onClick={handleViewReport}
        >
          View report
        </Button>
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <Button
            sx={{
              px: 2,
              borderRadius: "12px",
              backgroundColor: `${brand[600]} !important`,
              color: "white",
              fontWeight: "bold",
              visibility: saveLoading ? "hidden" : "visible",
              "&:hover": {
                backgroundColor: `${brand[700]} !important`,
              },
            }}
            disabled={saveLoading}
            onClick={handleSaveReport}
          >
            Save Report
          </Button>
          {saveLoading && (
            <CircularProgress
              size={30}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-14px",
                marginLeft: "-14px",
              }}
            />
          )}
        </Box>
      </Stack>
      <ValuesModal
        openModal={openValuesModal}
        setOpenModal={setOpenValuesModal}
        values={selectedValues}
        buttonLoading={false}
        valuesLoading={valuesLoading}
        onAddSelectedValues={addSelectedValuesToFilter}
        operator={selectedOperator}
        preSelectedValues={currentFilterValue}
      />
      <ConfirmModal
        openConfirmModal={showModal}
        setOpenConfirmModal={(open) => {
          if (open) setShowModal(true);
          else handleCancelNavigation();
        }}
        modalTitle="Unsaved changes"
        modalSubtitle="You have unsaved changes in your report configuration. Are you sure you want to proceed without saving?"
        confirmButtonClick={handleConfirmNavigation}
        confirmButtonText="Yes, don't save"
        cancelButtonText="No, continue editing"
      />
      <PreviewReportModal
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPreviewTemplateData(null);
        }}
        templateData={previewTemplateData}
        portalId={portalUid || null}
      />
    </Box>
  );
};
